export type OiDomainKey = 'command' | 'health' | 'growth' | 'operations' | 'intelligence';

export interface OiSubNavItem {
  key: string;
  label: string;
  path: string;
  /** Optional alert area keys for badge grouping */
  alertAreas?: string[];
}

export interface OiDomainNav {
  key: OiDomainKey;
  label: string;
  items: OiSubNavItem[];
}

export const OWNER_INTELLIGENCE_DOMAINS: OiDomainNav[] = [
  {
    key: 'command',
    label: 'Command',
    items: [
      { key: 'dashboard', label: 'Dashboard', path: '/owner-intelligence' },
      { key: 'group-gm', label: 'Group GM', path: '/owner-intelligence/group' },
      { key: 'memory', label: 'Executive Memory', path: '/owner-intelligence/memory', alertAreas: ['health'] },
      { key: 'meetings', label: 'Meetings', path: '/owner-intelligence/meetings' },
    ],
  },
  {
    key: 'health',
    label: 'Health',
    items: [
      { key: 'business-health', label: 'Business Health', path: '/owner-intelligence/business-health' },
      { key: 'financial-health', label: 'Financial Health', path: '/owner-intelligence/financial-health', alertAreas: ['finance'] },
      { key: 'kpis', label: 'KPI Performance', path: '/owner-intelligence/kpis' },
    ],
  },
  {
    key: 'growth',
    label: 'Growth',
    items: [
      { key: 'profit', label: 'Revenue & Profit', path: '/owner-intelligence/profit' },
      { key: 'cash', label: 'Cash & WC', path: '/owner-intelligence/cash', alertAreas: ['cash', 'finance'] },
      { key: 'customers', label: 'Customers', path: '/owner-intelligence/customers' },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    items: [
      { key: 'operations', label: 'Operations', path: '/owner-intelligence/operations', alertAreas: ['manufacturing', 'delivery'] },
      { key: 'supply-chain', label: 'Supply Chain', path: '/owner-intelligence/supply-chain', alertAreas: ['procurement', 'supply_chain'] },
    ],
  },
  {
    key: 'intelligence',
    label: 'Intelligence',
    items: [
      { key: 'strategy', label: 'Strategy', path: '/owner-intelligence/strategy' },
      {
        key: 'enterprise-strategy',
        label: 'Enterprise Strategy',
        path: '/owner-intelligence/enterprise-strategy',
        alertAreas: ['actions', 'risk'],
      },
      {
        key: 'enterprise-readiness',
        label: 'Enterprise Readiness',
        path: '/owner-intelligence/enterprise-readiness',
        alertAreas: ['risk'],
      },
      { key: 'actions', label: 'Actions & Decisions', path: '/owner-intelligence/actions', alertAreas: ['actions'] },
      { key: 'root-cause', label: 'Root Cause', path: '/owner-intelligence/root-cause' },
      { key: 'risk', label: 'Risk & Opportunities', path: '/owner-intelligence/risk', alertAreas: ['risk'] },
      {
        key: 'operational-intelligence',
        label: 'Operational Intelligence',
        path: '/owner-intelligence/operational-intelligence',
        alertAreas: ['risk', 'actions'],
      },
      {
        key: 'operational-actions',
        label: 'Operational Actions',
        path: '/owner-intelligence/operational-actions',
        alertAreas: ['actions'],
      },
      {
        key: 'digital-twin',
        label: 'Digital Twin',
        path: '/owner-intelligence/digital-twin',
        alertAreas: ['risk'],
      },
      {
        key: 'module-intelligence',
        label: 'Module Intelligence',
        path: '/owner-intelligence/module-intelligence',
      },
      {
        key: 'automation',
        label: 'Enterprise Automation',
        path: '/owner-intelligence/automation',
        alertAreas: ['actions'],
      },
      { key: 'scenarios', label: 'Scenarios', path: '/owner-intelligence/scenarios' },
      { key: 'advisor', label: 'AI Advisor', path: '/owner-intelligence/advisor' },
      { key: 'ai-governance', label: 'AI Governance', path: '/owner-intelligence/ai-governance' },
      {
        key: 'organizational-knowledge',
        label: 'Organizational Knowledge',
        path: '/owner-intelligence/organizational-knowledge',
      },
      { key: 'external-context', label: 'External Context', path: '/owner-intelligence/external-context' },
      { key: 'board-packs', label: 'Board & Briefings', path: '/owner-intelligence/board-packs' },
    ],
  },
];

const ALL_PATHS = OWNER_INTELLIGENCE_DOMAINS.flatMap((d) => d.items.map((i) => i.path));

export function resolveOiNavigation(pathname: string): {
  domain: OiDomainNav;
  subItem: OiSubNavItem;
} {
  const exact = ALL_PATHS.find((p) => pathname === p);
  if (exact) {
    for (const domain of OWNER_INTELLIGENCE_DOMAINS) {
      const sub = domain.items.find((i) => i.path === exact);
      if (sub) return { domain, subItem: sub };
    }
  }
  if (pathname.startsWith('/owner-intelligence')) {
    return { domain: OWNER_INTELLIGENCE_DOMAINS[0], subItem: OWNER_INTELLIGENCE_DOMAINS[0].items[0] };
  }
  return { domain: OWNER_INTELLIGENCE_DOMAINS[0], subItem: OWNER_INTELLIGENCE_DOMAINS[0].items[0] };
}

export function countAlertsForDomain(
  domain: OiDomainNav,
  alerts: Array<{ area?: string; severity?: string; status?: string }>
): number {
  const open = alerts.filter((a) => !a.status || a.status === 'open' || a.status === 'active');
  const critical = open.filter((a) => a.severity === 'critical' || a.severity === 'high');
  const areas = new Set(domain.items.flatMap((i) => i.alertAreas ?? []));
  if (!areas.size) return 0;
  return critical.filter((a) => areas.has(String(a.area ?? '').toLowerCase())).length;
}
